import React, { Dispatch, SetStateAction, useState } from "react"
import { useLazyQuery, useMutation, useQuery } from "@apollo/client"
import alertActions from "lib/store/services/Alert/AlertSlice"
import { SubmitHandler, useForm } from "react-hook-form"
import { InputCustomPaymentConfig } from "lib/types/generated/graphql-types"
import { useAppDispatch } from "lib/store"
import { hideEditSidebar } from "lib/store/services/editSidebar/slice"
import { Tab, TabList, TabPanel, Tabs } from "react-tabs"
import { PROVIDER_CATEGORY, PROVIDER_TYPE } from "lib/types/common"

import EditSidebar from "components/Ui/EditSidebar/EditSidebar"
import EditSidebarHeader from "components/Ui/EditSidebar/EditSidebarHeader"
import DeleteButton from "components/Ui/EditSidebar/DeleteButton"
import ConnectedStoreMarket from "components/Config/ConnectedStoreMarkets/ConnectedStoreMarket"
import PrimaryButton from "components/Ui/Buttons/PrimaryButton"
import Popup from "components/Ui/Popup"
import GET_CUSTOM_PAYMENT_CONFIG from "../../../../graphql/queries/config/Providers/CustomPayment/GetCustomPaymentConfig"
import { CREATE_OR_UPDATE_CUSTOM_PAYMENT_CONFIG } from "../../../../graphql/mutations/config/Providers/CustomPayment/CreateOrUpdateCustomPaymentConfig"
import { VALIDATE_CUSTOM_PAYMENT } from "../../../../graphql/queries/config/Providers/CustomPayment/ValidateCustomPaymentConfig"
import { DELETE_CUSTOM_PAYMENT_CONFIG } from "../../../../graphql/mutations/config/Providers/CustomPayment/DeleteCustomPaymentConfig"
import { CustomPaymentConfigInputs } from "../../../../components/Config/CustomPayment/CustomPaymentConfigInputs"
import { handleErrorMessages } from "helpers/errors"
import { WithAuth } from "../../../../components/Auth"
import { PermissionGroups } from "../../../../routes/constants"

type Props = {
  refetch: () => void
  editId: string
  setEditId: Dispatch<SetStateAction<string>>
}

interface ExtendedInputCustomPaymentConfig {
  id: string
  customPaymentConfig: InputCustomPaymentConfig
}

export const EditCustomPaymentConfig = ({ refetch, editId, setEditId }: Props) => {
  const [validationError, setValidationError] = useState<boolean>(false)
  const dispatch = useAppDispatch()

  const { data } = useQuery(GET_CUSTOM_PAYMENT_CONFIG, {
    variables: { id: editId }
  })
  const closeSidebar = () => {
    setEditId("")
    dispatch(hideEditSidebar())
  }

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<ExtendedInputCustomPaymentConfig>({ mode: "onBlur" })

  const [updateCustomPaymentConfig, { loading: createLoading }] = useMutation(
    CREATE_OR_UPDATE_CUSTOM_PAYMENT_CONFIG,
    {
      onCompleted: () => {
        dispatch(hideEditSidebar())
        reset(customPaymentConfig)
        dispatch(
          alertActions.actions.createAlert({
            message: "Custom Payment configuration successfully updated",
            type: "success"
          })
        )
        refetch()
      },
      onError: () => {
        dispatch(
          alertActions.actions.createAlert({
            type: "error"
          })
        )
      }
    }
  )
  const customPaymentConfig = data?.getCustomPaymentConfig
  const { id } = customPaymentConfig

  const [validateCustomPayment, { loading }] = useLazyQuery(VALIDATE_CUSTOM_PAYMENT, {
    onError: (error) => handleErrorMessages(error)
  })

  const onSubmit: SubmitHandler<ExtendedInputCustomPaymentConfig> = (data) => {
    const { baseUrl, authUrl, clientSecret, clientId } = data.customPaymentConfig
    validateCustomPayment({
      variables: {
        baseUrl,
        authUrl,
        clientSecret,
        clientId
      }
    }).then((response) => {
      if (response.data.Configuration.validate.customPayment.valid) {
        updateCustomPaymentConfig({
          variables: {
            id: editId,
            customPaymentConfig: {
              baseUrl,
              authUrl,
              clientId,
              clientSecret
            }
          }
        })
      } else {
        setValidationError(true)
      }
    })
  }

  if (!customPaymentConfig) return null

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <EditSidebar cancelEvent={() => closeSidebar()}>
        <EditSidebarHeader title={id} id={id} cancelEvent={() => closeSidebar()}>
          <WithAuth requiredGroups={PermissionGroups.providers.write}>
            <PrimaryButton type="submit" loading={createLoading || loading}>
              Save
            </PrimaryButton>
            <DeleteButton
              id={id}
              name={id}
              entity={"Custom Payment configuration"}
              redirectUrl="/settings/providers/custom-payment"
              mutation={DELETE_CUSTOM_PAYMENT_CONFIG}
              refetch={refetch}
              postDeleteFunction={() => setEditId("")}
              secureDelete={true}
            />
          </WithAuth>
        </EditSidebarHeader>
        {validationError ? (
          <Popup
            title="Validation error"
            subtitle="Couldn't validate provided credentials with Custom Payment"
            hideOkButton={true}
            cancelButtonText={"Close"}
            handleCloseClick={() => setValidationError(false)}
          />
        ) : (
          <></>
        )}
        <Tabs>
          <TabList>
            <Tab>General</Tab>
            <Tab>Connected store markets</Tab>
          </TabList>
          <TabPanel>
            <CustomPaymentConfigInputs
              control={control}
              errors={errors}
              defaultValues={customPaymentConfig}
              showId={false}
            />
          </TabPanel>
          <TabPanel>
            <ConnectedStoreMarket
              providerId={customPaymentConfig.id}
              providerType={PROVIDER_TYPE.CUSTOM_PAYMENT}
              providerCategory={PROVIDER_CATEGORY.PAYMENT}
            />
          </TabPanel>
        </Tabs>
      </EditSidebar>
    </form>
  )
}
